import { useRef, useState } from "react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-coal border border-white/10 focus:border-electric-bright outline-none px-4 py-3.5 text-sm text-white placeholder:text-gray-600 transition-colors rounded-none";
const labelCls = "block text-[10px] font-bold uppercase tracking-[0.25em] text-gray-500 mb-2";

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  vehicle: "",
  fuel_type: "",
  registration: "",
  vin: "",
  service: "",
  preferred_date: "",
  preferred_time: "",
  message: "",
};

const errMsg = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x.msg || "").replace(/^Value error, /, "")).join(" ");
  return "Something went wrong. Please try again.";
};

export default function Booking({ content }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const formRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);

    try {
      await axios.post(`${API}/bookings`, { ...form, service: form.service || "General enquiry" });
    } catch (err) {
      toast.error(errMsg(err));
      setSending(false);
      return;
    }

    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
      );
      toast.success("Enquiry sent — check your inbox for a confirmation email.");
    } catch (err) {
      console.error("EmailJS send failed:", err);
      toast.warning("Enquiry received, but the confirmation email failed to send — we've still got your details and will be in touch.");
    }

    setForm(EMPTY);
    setSending(false);
  };

  return (
    <section id="booking" className="scroll-mt-20 py-24 sm:py-32 bg-coal border-y border-white/5" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.3fr] gap-14 lg:gap-20">
        <div>
          <Reveal>
            <Overline>Booking & Enquiries</Overline>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight uppercase leading-[1.02] text-white">
              Book your service
            </h2>
            <p className="mt-6 text-sm sm:text-base text-gray-400 leading-relaxed max-w-md">
              Tell us about your vehicle and what you're looking for. We'll come back to you to confirm availability and answer any questions before anything is booked in.
            </p>
            <div className="mt-10 space-y-3 text-sm text-gray-400">
              <p><span className="text-electric-bright font-semibold">Email — </span>{content.contact.email}</p>
              <p><span className="text-electric-bright font-semibold">Instagram — </span>@{content.contact.instagram}</p>
              <p><span className="text-electric-bright font-semibold">Based in — </span>{content.contact.location}</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form ref={formRef} onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="booking-form">
            <div>
              <label className={labelCls} htmlFor="bk-name">Name *</label>
              <input id="bk-name" name="name" data-testid="booking-name-input" required className={inputCls} placeholder="Your full name" value={form.name} onChange={set("name")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-phone">Phone *</label>
              <input id="bk-phone" name="phone" data-testid="booking-phone-input" required type="tel" className={inputCls} placeholder="07123 456789" value={form.phone} onChange={set("phone")} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="bk-email">Email *</label>
              <input id="bk-email" name="email" data-testid="booking-email-input" required type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={set("email")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-vehicle">Vehicle make & model *</label>
              <input id="bk-vehicle" name="vehicle" data-testid="booking-vehicle-input" required className={inputCls} placeholder="e.g. BMW 330d" value={form.vehicle} onChange={set("vehicle")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-fuel">Fuel type *</label>
              <select id="bk-fuel" name="fuel_type" data-testid="booking-fuel-select" required className={inputCls} value={form.fuel_type} onChange={set("fuel_type")}>
                <option value="" disabled>Select fuel type</option>
                <option value="Petrol">Petrol</option>
                <option value="Diesel">Diesel</option>
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-reg">Registration</label>
              <input id="bk-reg" data-testid="booking-registration-input" className={`${inputCls} uppercase`} placeholder="e.g. AB21 CDE" value={form.registration} onChange={set("registration")} />
              <input type="hidden" name="registration" value={form.registration.trim() || "—"} />
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-vin">VIN number <span className="text-electric-bright normal-case tracking-normal">(optional but preferred)</span></label>
              <input id="bk-vin" data-testid="booking-vin-input" className={`${inputCls} uppercase`} placeholder="17-character VIN" maxLength={17} value={form.vin} onChange={set("vin")} />
              <input type="hidden" name="vin" value={form.vin.trim() || "—"} />
              <p className="mt-1.5 text-[11px] text-gray-600">Found at the base of your windscreen or in your V5C logbook — it lets us quote accurately for your exact vehicle.</p>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="bk-service">Desired service *</label>
              <select id="bk-service" name="service" data-testid="booking-service-select" required className={inputCls} value={form.service} onChange={set("service")}>
                <option value="" disabled>Select a service</option>
                {content.services.map((s) => (
                  <option key={s.id} value={s.title}>{s.title}</option>
                ))}
                <option value="Quote / individual assessment">Quote / individual assessment</option>
                <option value="General enquiry">General enquiry</option>
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-date">Preferred date</label>
              <input id="bk-date" data-testid="booking-date-input" type="date" min={new Date().toISOString().split("T")[0]} className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} />
              <input
                type="hidden"
                name="preferred_date"
                value={
                  form.preferred_date
                    ? new Date(form.preferred_date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
                    : "—"
                }
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-time">Preferred time</label>
              <select id="bk-time" data-testid="booking-time-select" className={inputCls} value={form.preferred_time} onChange={set("preferred_time")}>
                <option value="">No preference</option>
                <option value="Morning (before 12pm)">Morning (before 12pm)</option>
                <option value="Afternoon (12pm – 5pm)">Afternoon (12pm – 5pm)</option>
              </select>
              <input type="hidden" name="preferred_time" value={form.preferred_time || "—"} />
              <p className="mt-1.5 text-[11px] text-gray-600">We'll take this into consideration and try to get you an appointment as close to your preferred time as possible.</p>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="bk-message">Anything else we should know?</label>
              <textarea id="bk-message" data-testid="booking-message-input" rows={4} className={`${inputCls} resize-none`} placeholder="Current mods, issues you've noticed, questions…" value={form.message} onChange={set("message")} />
              <input type="hidden" name="message" value={form.message.trim() || "—"} />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={sending}
                data-testid="booking-submit-button"
                className="inline-flex items-center gap-3 bg-electric hover:bg-electric-bright disabled:opacity-60 text-white text-sm font-bold uppercase tracking-[0.18em] px-9 py-4 transition-colors shadow-[0_0_24px_rgba(6,69,243,0.35)]"
              >
                {sending ? "Sending…" : "Send Enquiry"} <Send size={15} />
              </button>
              <p className="mt-4 text-xs text-gray-600 leading-relaxed max-w-lg">
                Submitting this form sends an enquiry — it's not a confirmed booking. We'll contact you to arrange a time that works.
              </p>
              <p className="mt-2 text-xs text-gray-600 leading-relaxed max-w-lg" data-testid="booking-privacy-note">
                Your details are only used to respond to your enquiry — never shared with third parties and never used for marketing.
              </p>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
